import { useMemo } from "react";
import type { Process } from "@archlang/scene/arch-model";
import {
  estimateProcess,
  buildLatencyEnv,
  formatEstimate,
  type ResolvedModel,
  type ProcessModel,
} from "@archlang/engine";
import { processKey } from "../processKey.js";

/**
 * The process-header latency badge string (`120ms – 2s · N unestimated`) for
 * the active process, or null when there's nothing to show. Partial-model
 * validity: a process with **no** priced timing shows no badge (opting out of
 * latency modeling is fine); a **partly** timed process shows `… – ???` (the
 * gaps are the TODO). Pure + memoized; a fold exception degrades to null.
 */
export function useProcessLatencyBadge(
  resolvedModel: ResolvedModel | null,
  processModels: readonly ProcessModel[],
  activeProcessId: string | null,
): string | null {
  return useMemo(() => {
    if (!resolvedModel || !activeProcessId) return null;
    const pm = processModels.find((p) => processKey(p as unknown as Process) === activeProcessId);
    if (!pm) return null;
    try {
      const est = estimateProcess(pm, buildLatencyEnv(resolvedModel, pm));
      // No estimation in EITHER dimension → no badge (opting out is fine).
      if (est.timed.system.priced <= 0 && est.timed.business.priced <= 0) return null;
      return formatEstimate(est);
    } catch {
      return null;
    }
  }, [resolvedModel, processModels, activeProcessId]);
}
